import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useGamification } from "@/contexts/GamificationContext";
import { useGamificationStore } from "@/stores/useGamificationStore";
import { useTimeTrackingStore } from "@/stores/useTimeTrackingStore";
import { fetchUserActivity } from "@/lib/db";
import type { GamificationStats } from "@/stores/useGamificationStore";

/** Mastery medal definition: study-time based achievements (8 medals on Statistics) */
export interface MasteryMedalDef {
  id: string;
  /** Threshold in hours */
  hours: number;
  nameKey: string;
  color: string;
  glowColor: string;
}

export const MASTERY_MEDAL_DEFINITIONS: MasteryMedalDef[] = [
  { id: "medalFirstStep", hours: 1, nameKey: "medalFirstStep", color: "#CD7F32", glowColor: "rgba(205,127,50,0.5)" },
  { id: "medalDiligentStudent", hours: 10, nameKey: "medalDiligentStudent", color: "#C0C0C0", glowColor: "rgba(192,192,192,0.5)" },
  { id: "medalLanguageAthlete", hours: 50, nameKey: "medalLanguageAthlete", color: "#FFD700", glowColor: "rgba(255,215,0,0.5)" },
  { id: "medalTimeMaster", hours: 72, nameKey: "medalTimeMaster", color: "#a78bfa", glowColor: "rgba(167,139,250,0.5)" },
  { id: "medalMasterStudyDo", hours: 100, nameKey: "medalMasterStudyDo", color: "#00e5ff", glowColor: "rgba(0,229,255,0.5)" },
  { id: "medalLegend", hours: 500, nameKey: "medalLegend", color: "#FFD700", glowColor: "rgba(255,215,0,0.6)" },
  { id: "medalPolyglot", hours: 1000, nameKey: "medalPolyglot", color: "#b9f2ff", glowColor: "rgba(0,212,255,0.6)" },
  { id: "medalAbsolute", hours: 10000, nameKey: "medalAbsolute", color: "#c084fc", glowColor: "rgba(192,132,252,0.6)" },
];

export interface MasteryMedalProgress {
  medal: MasteryMedalDef;
  currentHours: number;
  requiredHours: number;
  isUnlocked: boolean;
  progressPercent: number;
  /** Formatted "X/Y h" for display */
  progressLabel: string;
}

/** Aggregates study time from DB activity + current session, syncs to gamification store */
function useTotalStudyMinutes(): { totalStudyMinutes: number; isLoading: boolean } {
  const { user } = useAuth();
  const stats = useGamificationStore((s) => s.stats);
  const syncTimeTotals = useGamificationStore((s) => s.syncTimeTotals);
  const studySeconds = useTimeTrackingStore((s) => s.studySeconds);
  const [activityByDate, setActivityByDate] = useState<Record<string, { minutes: number; total_site_minutes: number }>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }
    fetchUserActivity(user.id, "all")
      .then((data) => {
        const out: Record<string, { minutes: number; total_site_minutes: number }> = {};
        for (const [date, day] of Object.entries(data)) {
          out[date] = {
            minutes: day.minutes ?? 0,
            total_site_minutes: day.total_site_minutes ?? 0,
          };
        }
        setActivityByDate(out);
      })
      .catch(() => setActivityByDate({}))
      .finally(() => setIsLoading(false));
  }, [user?.id]);

  const totalStudyMinutes = useMemo(() => {
    let total = 0;
    const todayStr = new Date().toISOString().slice(0, 10);
    const persistedStudyMinutes = Math.max(Math.floor(studySeconds / 60), studySeconds > 0 ? 1 : 0);

    for (const [date, day] of Object.entries(activityByDate)) {
      const mins = day.minutes ?? 0;
      if (date === todayStr) {
        total += Math.max(mins, persistedStudyMinutes);
      } else {
        total += mins;
      }
    }

    if (Object.keys(activityByDate).length === 0 && persistedStudyMinutes > 0) {
      total = Math.max(total, stats.totalStudyMinutes + persistedStudyMinutes);
    }

    if (Object.keys(activityByDate).length > 0) {
      const totalSite = Object.values(activityByDate).reduce((s, d) => s + (d.total_site_minutes ?? 0), 0);
      syncTimeTotals(total, totalSite);
    }

    return Object.keys(activityByDate).length > 0 ? total : stats.totalStudyMinutes + persistedStudyMinutes;
  }, [activityByDate, studySeconds, stats.totalStudyMinutes, syncTimeTotals]);

  return { totalStudyMinutes, isLoading };
}

/** Hook for the 8 Mastery Medals (Statistics dashboard). Returns progress for each medal. */
export function useMasteryMedals(): {
  medals: MasteryMedalProgress[];
  totalStudyHours: number;
  isLoading: boolean;
} {
  const { totalStudyMinutes, isLoading } = useTotalStudyMinutes();
  const totalStudyHours = totalStudyMinutes / 60;

  const medals = useMemo((): MasteryMedalProgress[] => {
    return MASTERY_MEDAL_DEFINITIONS.map((medal) => {
      const currentHours = totalStudyHours;
      const requiredHours = medal.hours;
      const isUnlocked = currentHours >= requiredHours;
      const progressPercent = requiredHours > 0 ? Math.min(100, (currentHours / requiredHours) * 100) : 100;
      const progressLabel = `${Math.floor(currentHours).toLocaleString()}/${requiredHours.toLocaleString()}`;
      return {
        medal,
        currentHours,
        requiredHours,
        isUnlocked,
        progressPercent,
        progressLabel,
      };
    });
  }, [totalStudyHours]);

  return { medals, totalStudyHours, isLoading };
}

/** Badge definition for Achievements Dashboard (12 badges with tiered progression) */
export type BadgeStatKey = keyof GamificationStats | "currentStreak";

export interface BadgeProgress {
  badgeId: string;
  statKey: BadgeStatKey;
  currentValue: number;
  /** First threshold = bronze; unlocked when currentValue >= first threshold */
  firstThreshold: number;
  nextThreshold: number | null;
  isUnlocked: boolean;
  progressPercent: number;
  tierIndex: number;
  isMaxTier: boolean;
}

const BADGE_THRESHOLDS: Record<string, number[]> = {
  lexiconMaster: [50, 500, 2500, 10000, 25000],
  streakManiac: [7, 30, 90, 180, 365],
  aiPioneer: [50, 250, 1000, 5000, 10000],
  perfectStudent: [5, 25, 100, 250, 500],
  encyclopedia: [500, 5000, 25000, 100000, 500000],
  earlyBird: [10, 50, 200, 500, 1000],
  knowledgeArchitect: [5, 20, 50, 100, 200],
  visualizer: [20, 100, 500, 2000, 5000],
  nightOwl: [10, 50, 200, 500, 1000],
  crystalMogul: [1000, 10000, 50000, 250000, 1000000],
  studyTimeMaster: [60, 360, 1440, 7200, 43200],
  appTimeMaster: [120, 720, 4320, 21600, 86400],
};

const BADGE_STAT_KEYS: Record<string, BadgeStatKey> = {
  lexiconMaster: "wordsLearned",
  streakManiac: "currentStreak",
  aiPioneer: "aiWordsGenerated",
  perfectStudent: "testsPassed",
  encyclopedia: "cardsViewed",
  earlyBird: "earlyBirdSessions",
  knowledgeArchitect: "decksCreated",
  visualizer: "cardsWithImages",
  nightOwl: "nightOwlSessions",
  crystalMogul: "totalCrystalsEarned",
  studyTimeMaster: "totalStudyMinutes",
  appTimeMaster: "totalSiteMinutes",
};

/** Hook for Achievements Dashboard badges. Maps real stats to unlock/progress. */
export function useBadgeAchievements(): BadgeProgress[] {
  const { stats, currentStreak } = useGamification();

  return useMemo((): BadgeProgress[] => {
    const getStatValue = (key: BadgeStatKey): number => {
      if (key === "currentStreak") return currentStreak;
      return stats[key] ?? 0;
    };

    return Object.entries(BADGE_STAT_KEYS).map(([badgeId, statKey]) => {
      const thresholds = BADGE_THRESHOLDS[badgeId] ?? [0];
      const currentValue = getStatValue(statKey);
      const firstThreshold = thresholds[0] ?? 0;
      let tierIndex = -1;
      for (let i = 0; i < thresholds.length; i++) {
        if (currentValue >= thresholds[i]) tierIndex = i;
      }
      const isUnlocked = tierIndex >= 0;
      const nextThreshold = tierIndex < thresholds.length - 1 ? thresholds[tierIndex + 1] : null;
      const progressPercent = nextThreshold
        ? Math.min(100, (currentValue / nextThreshold) * 100)
        : isUnlocked ? 100 : 0;

      return {
        badgeId,
        statKey,
        currentValue,
        firstThreshold,
        nextThreshold: nextThreshold ?? null,
        isUnlocked,
        progressPercent,
        tierIndex: Math.max(0, tierIndex),
        isMaxTier: tierIndex >= thresholds.length - 1,
      };
    });
  }, [stats, currentStreak]);
}
